import { isEqual } from "lodash"
import { exhaustiveCheck } from "@helpers/exhaustiveCheck"

export type Option<T> = Some<T> | None
export type Some<T> = { readonly __tag: 'Some', readonly value: T }
export type None = { readonly __tag: 'None' }
export type Defined<T> = Exclude<T, null | undefined | void>

/**
 * Applies a utility type to each value in an object type
 * @example
 * // Creates type { name: readonly string, age: readonly number }
 * type ReadonlyPerson = MapValues<{ name: string, age: number }, Readonly<T>>;
 * 
 * // Creates type { name: string[], age: number[] }
 * type ArrayPerson = MapValues<{ name: string, age: number }, Array<T>>;
 */
type MapValues<T extends object, U> = {
    [K in keyof T]: T[K] extends infer V
    ? (U extends (arg: any) => any
        ? U extends (arg: V) => infer R
        ? R
        : never
        : U)
    : never
}

export type Optional<T extends object> = MapValues<T, Option<T[keyof T]>>

/**
 * Suggested usage:
 * const { match, toOption, none, isSome } = Option
 */
export namespace Option {
    /**
     * Type guard for Some.
     * @param option Option to check
     * @returns true if Option is Some
     */
    export const isSome = <T>(option: Option<T>): option is Some<T> => option.__tag === 'Some'
    /**
     * Type guard for Some specific value.
     * @param option generic Option
     * @param value value of Some<T> to check
     * @returns true if Option is Some and value is equal to the value of Some
     */
    export const isSomeValue = <T, V extends T>(option: Option<T>, value: V): boolean => match(option, {
        some: (some) => isEqual(some, value),
        none: () => false,
    })
    /**
     * Type guard for Some specific value.
     * @param option generic Option
     * @param assessment function to check if Some<T> is equal to the value of Some
     * @returns true if Option is Some and value is equal to the value of Some
     */
    export const hasSomeValue = <T>(option: Option<T>, assessment: ((some: T) => boolean)): boolean => match(option, {
        some: (some) => assessment(some),
        none: () => false,
    })
    /**
     * Smart constructor for Option type none.
     */
    export const none: Option<never> = { __tag: 'None' }
    /**
     * Convert a potentially null or undefined value to an Option
     * @param value value to convert to Option
     * @returns an Option of value's type
     */
    export const toOption = <T>(value: T | undefined | null): Option<T> => value === undefined || value === null ? none : { __tag: 'Some', value }
    /**
     * Match an Option with a function for each case.
     * @param option Option to match
     * @param someFn function to call if Option is Some
     * @param noneFn function to call if Option is None
     * @returns the result of the function called
     */
    export const match: <T, S, N>(
        option: Option<T>,
        f: {
            some: (some: T) => Defined<S>
            none: () => Defined<N>
        }
    ) => Defined<S | N> =
        (option, f) => {
            switch (option.__tag) {
                case 'Some': return f.some(option.value)
                case 'None': return f.none()
                default: return exhaustiveCheck(option)
            }
        }
    /**
     * This function is used to get the value of an Option or a fallback value if the Option is None
     * @param n generic Option
     * @param elseValue fallback value if Option is None
     * @returns generic type T
     */
    export const getOrElse = <T>(n: Option<Defined<T>> | null | undefined, elseValue: Defined<T>) =>
        n == null || n == undefined
            ? elseValue
            : match(n, {
                some: (value) => value,
                none: () => elseValue,
            })
    /**
     * This method is used in Angular's control flow to convert Signal<Option<T>> to a truthy/falsy value.
     * eg. `@if` (orUndefined(value()); as value) { ... }  where value is a Signal<Option<T>>
     * Bind a reference to Option in your Component to use this method in your template.
     * @param n Option to convert to a defined value or undefined
     * @returns T | undefined
     */
    export const orUndefined = <T>(n: Option<Defined<T>>): T | undefined => n.__tag === 'Some' ? n.value : undefined

    export const mapOption = <T, U>(option: Option<T>, f: (t: T) => U): Option<U> =>
        match(option, {
            some: (value) => toOption(f(value)),
            none: () => none,
        })

    export const complete = (fn: Function) => {
        fn()
        return none
    }

    /**
     * Performs an operation on the value of an Option if it is Some.
     */
    export const onSome = <T, U>(option: Option<T>, f: (value: T) => Option<U>): Option<U> =>
        match(option, {
            some: (value) => f(value),
            none: () => none,
        })
    /**
     * Performs an operation on the value of an Option if it is Some.
     * This is a more generic version of onSome that allows for null or undefined values.
     * It is used to convert a value to an Option and then perform an operation on it.
     * @param input value to convert to Option
     * @param f function to call if Option is Some
     * @returns Option of the result of the function call
     * */
    export const ifSome = <T, U>(input: Defined<T> | null | undefined, f: (value: T) => Option<U>): Option<U> =>
        match(toOption(input), {
            some: (value) => f(value),
            none: () => none,
        })
}
const { toOption, none, isSome } = Option
export class OptionMap<TKey extends string | number, T> {
    constructor() { }

    private readonly _map = new Map<TKey, Some<T>>()
    /**
     * Get the Option of T for a given key.
     */
    readonly get = (key: TKey): Option<T> => this._map.get(key) ?? none
    /**
     * Adds a new element with a specified key and value to the Map. If an element with the same key already exists, the element will be updated.
     */
    readonly set = (key: TKey, value: T | undefined | null) => {
        const v = toOption(value)
        if (isSome(v)) {
            this._map.set(key, v)
            return v
        }
        return none
    }
    readonly delete = (key: TKey) => {
        this._map.delete(key)
        return none
    }

    readonly clear = () => this._map.clear()

    readonly has = (key: TKey) => this._map.has(key)

    readonly values = () => Array.from(this._map.values()).map(x => x.value)

    readonly clone = () => {
        const clone = new OptionMap<TKey, T>()
        this._map.forEach((value, key) => {
            clone.set(key, value.value)
        })
        return clone
    }
}